import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from '@phosphor-icons/react';

interface ThankYouPanelProps {
  ownerName?: string;
  onSubmitAnother: () => void;
}

/**
 * Success state shown in place of the form after a 201 (F4/F7). Moves focus to
 * the heading and announces via aria-live.
 */
export function ThankYouPanel({ ownerName, onSubmitAnother }: ThankYouPanelProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      className="rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-100"
      aria-live="polite"
    >
      <CheckCircle className="mx-auto h-14 w-14 text-brand-blue" aria-hidden="true" />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="mt-4 text-2xl font-bold text-brand-navy outline-none"
      >
        Thanks{ownerName ? `, ${ownerName.split(' ')[0]}` : ''}! We've got your details.
      </h2>
      <p className="mx-auto mt-3 max-w-md text-gray-600">
        Our partnerships team will review your property and reach out within{' '}
        <strong>2 business days</strong> to walk you through the next steps.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/"
          className="rounded-xl border border-primary-200 px-5 py-2.5 text-sm font-semibold text-brand-navy hover:bg-brand-blue-light"
        >
          Back to home
        </Link>
        <button
          type="button"
          onClick={onSubmitAnother}
          className="rounded-xl bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-blue-dark"
        >
          Submit another property
        </button>
      </div>
    </div>
  );
}
